import React from "react";

/**
 * Aruh Koncepts — "Precision Lead Generation" Case Study
 * -------------------------------------------------------
 * Single-file React component reproducing the case-study page design.
 * Desktop / laptop layout matches the source PDF 1:1.
 * Tablet (<=1024px) and Mobile (<=640px) layouts adapt fluidly.
 *
 * Intentionally EXCLUDES the site header/nav, ContactSection, and
 * Footer — this component is only the case-study body, meant to be
 * dropped between your existing <Header /> and <ContactSection /> /
 * <Footer /> on the page/route.
 *
 * ASSETS: place the images shipped alongside this file into an
 * "assets" folder next to this component:
 *   assets/hero.jpg
 *   assets/creative-1.jpg
 *   assets/creative-2.jpg
 *
 * FONTS: Playfair Display (headings) + Lato (body) are pulled from
 * Google Fonts via AruhKonceptsCaseStudy.css — no extra setup needed.
 *
 * STYLES: previously an inline <style>{CSS}</style> block, now moved
 * to its own file: AruhKonceptsCaseStudy.css (imported below). No
 * rules were changed in the move — see that file's header comment
 * for the one syntax fix that was required (invalid "//" comments).
 *
 * GLOBAL STYLES: this component still relies on the project's global
 * index.css for the base reset (*, *::before, *::after) and the
 * default body font-smoothing — those rules are NOT duplicated here.
 * Component-specific design tokens (colors, max-width) are scoped
 * under .pl-page instead of :root so they never leak into the rest
 * of the app.
 */

import "./AruhMarketingCaseStudy.css";

import heroImg from "../../assets/Marketing/Aruhkoncept/aruhkonceptherosection.png";
import creative1Img from "../../assets/Marketing/Aruhkoncept/Aruhkonceptcreative1section.png";
import creative2Img from "../../assets/Marketing/Aruhkoncept/AruhKonceptcreative2section.png";

/* ------------------------------------------------------------------ */
/* Data                                                                 */
/* ------------------------------------------------------------------ */

const TAGS = ["Digital Marketing", "Hyderabad", "6 months"];

const OVERVIEW_META = [
  { label: "Client", value: "Aruh Koncepts" },
  { label: "Location", value: "Hyderabad" },
  { label: "Duration", value: "6 months" },
  { label: "Services", value: "Lead Generation, Meta Ads, SMM" },
];

const TARGETING = [
  {
    title: "Meta Ads: ABO with Job Title Targeting",
    kind: "list",
    items: [
      "Senior Corporate Executives (CEOs, Directors, VPs)",
      "Business Owners & Entrepreneurs",
      "Medical Professionals",
      "Tech & IT Leadership",
      "Real Estate Leaders",
    ],
  },
  {
    title: "Exclusion Strategy",
    kind: "text",
    text: "Aggressively filtered students, entry-level positions, budget-focused interests, and non-affluent areas.",
  },
  {
    title: "Geographic Focus",
    kind: "text",
    text: "Targeted only affluent Hyderabad neighborhoods — Jubilee Hills, Banjara Hills, Gachibowli.",
  },
  {
    title: "Transparency as Filter",
    kind: "text",
    text: "Communicated budget ranges openly (“₹15L–₹50L+”) to self-select qualified prospects.",
  },
];

const CONTENT_TYPES = [
  {
    code: "UGC",
    text: "Client testimonials, homeowner tours, authentic reveal reactions",
  },
  {
    code: "FGC",
    text: "Founder presenting design philosophy, process, project stories",
  },
  {
    code: "EGC",
    text: "Technical content, budget guidance, material education",
  },
];

const IMPACT_STATS = [
  {
    label: "Qualified Leads/Month",
    value: "27",
    sub: "from 3",
    note: "800% increase from baseline",
  },
  {
    label: "Qualification Rate",
    value: "71%",
    sub: "from 5%",
    note: "vs. 5% baseline",
  },
  {
    label: "Cost Per Qualified Lead",
    value: "₹478",
    sub: "",
    note: "87% reduction in cost",
  },
  {
    label: "Junk Lead Elimination",
    value: "84%",
    sub: "",
    note: "Dramatic reduction in unqualified leads",
  },
];

/* ------------------------------------------------------------------ */
/* Section 1 — Overview                                                 */
/* ------------------------------------------------------------------ */

function OverviewSection() {
  return (
    <section className="pl-section pl-overview-section">
      <div className="pl-overview-head">
        <div className="pl-tags">
          {TAGS.map((t) => (
            <span className="pl-tag" key={t}>
              {t}
            </span>
          ))}
        </div>
        <h1 className="digital-casestudy-heading">Precision Lead Generation for Aruh Koncepts</h1>
        <p className="pl-subtitle">Lead Generation, Meta Ads, SMM</p>
      </div>

      <div className="pl-hero-banner">
        <img
          src={heroImg}
          alt="Aruh Koncepts — More than interiors, we craft experiences"
        />
      </div>

      <div className="pl-overview-grid">
        <div className="pl-overview-meta">
          <span className="pl-eyebrow">Project Overview</span>
          <dl>
            {OVERVIEW_META.map((m) => (
              <div key={m.label}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="pl-overview-desc">
          <p>
            A precision-targeted Meta advertising and social media marketing
            campaign that transformed lead quality for a premium interior
            design studio — eliminating junk leads and driving 800% more
            qualified prospects.
          </p>
        </div>
      </div>

      <div className="pl-challenge">
        <span className="pl-eyebrow">The Challenge</span>
        <p>
          Aruh Koncepts, a premium interior design studio in Hyderabad,
          faced a critical lead quality problem: 50-70 monthly leads but
          only 5% qualified as serious prospects. Sales resources were
          wasted filtering budget shoppers and information seekers.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Section 2 — Strategy + Results                                       */
/* ------------------------------------------------------------------ */

function StrategyResultsSection() {
  return (
    <section className="pl-section pl-strategy-results-section">
      <div className="pl-strategy-dark">
        <div className="pl-strategy-intro">
          <span className="pl-eyebrow">The Strategy</span>
          <p>
            Grownex implemented precision-targeted Meta advertising with
            Social Media Marketing and Google My Business optimization,
            eliminating junk leads through strategic targeting and content.
          </p>
        </div>

        <div className="pl-targeting-grid">
          {TARGETING.map((card) => (
            <div className="pl-dark-card" key={card.title}>
              <h4>{card.title}</h4>
              {card.kind === "list" ? (
                <ul>
                  {card.items.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              ) : (
                <p>{card.text}</p>
              )}
            </div>
          ))}
        </div>

        <div className="pl-content-strategy">
          <h3 className="pl-content-strategy-title">
            Content Strategy: UGC, FGC &amp; EGC
          </h3>
          <p className="pl-content-strategy-note">
            Aligned with Meta's Andromeda algorithm
          </p>

          <div className="pl-content-grid">
            {CONTENT_TYPES.map((c) => (
              <div className="pl-dark-card pl-content-card" key={c.code}>
                <span className="pl-content-code">{c.code}</span>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pl-smm-card">
          <h4>SMM &amp; GMB</h4>

          <ul>
            <li>Instagram-focused content (5–7 Reels weekly)</li>
            <li>Portfolio showcases with high-quality imagery</li>
            <li>Optimized GMB with reviews and local SEO</li>
          </ul>
        </div>

      </div>

      <div className="pl-results">
        <span className="pl-eyebrow">The Results</span>
        <h3>Measurable Impact</h3>

        <div className="pl-stats-grid">
          {IMPACT_STATS.map((s) => (
            <div className="pl-stat" key={s.label}>
              <span className="pl-stat-label">{s.label}</span>
              <span className="pl-stat-value">
                {s.value}
                {s.sub && <span className="pl-stat-sub"> {s.sub}</span>}
              </span>
              <span className="pl-stat-note">{s.note}</span>
            </div>
          ))}
        </div>

        <div className="pl-results-footnotes">
          <div  style={{
    backgroundColor: "var(--color-grey-98, #FAFAFA)",
    border: "1px solid #FAFAFA",padding:"15px",borderRadius:"10px"
  }} >
            <span className="pl-block-label">Top Performers</span>
            <p>
              Medical professionals (81% qualification), Senior executives
              (78% qualification, highest project values)
            </p>
          </div>
          <div  style={{
    backgroundColor: "var(--color-grey-98, #FAFAFA)",
    border: "1px solid #FAFAFA",padding:"15px",borderRadius:"10px"
  }} >
            <span className="pl-block-label">Content Highlight</span>
            <p>
              UGC achieved 8.7% engagement with lowest CPM, outperforming
              polished creative.
            </p>
          </div>
        </div>
      </div>

      <div className="pl-cta-band">
        <span className="pl-eyebrow pl-eyebrow-light">Results that speak</span>
        <h2>Precision targeting. Real business outcomes.</h2>
        <p>
          Every campaign we build is designed to deliver qualified leads and
          measurable growth for Aruh Koncepts.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Section 3 — Creative Work                                            */
/* ------------------------------------------------------------------ */

function CreativeWorkSection() {
  return (
    <section className="pl-section pl-creative-section">
      <h3 className="pl-creative-title">Creative Work</h3>

      <div className="pl-creative-grid">
        <div className="pl-creative-main">
          <img
            src={creative1Img}
            alt="Aruh Koncepts ad creative — luxury home interiors in Hyderabad"
          />
        </div>

      </div>
      <div className="pl-creative-grid">
        <div className="pl-creative-side">
          <img
            src={creative2Img}
            alt="Aruh Koncepts ad creative — luxury bath interiors"
          />
        </div>
      </div>

    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Root component                                                       */
/* ------------------------------------------------------------------ */

export default function AruhKonceptsCaseStudy() {
  return (
    <div className="pl-page">
      <OverviewSection />
      <StrategyResultsSection />
      <CreativeWorkSection />
    </div>
  );
}